"use client"

import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslations } from "next-intl"
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query"
import debounce from "debounce"
import { useRouter, searchResultClick, searchSubmit } from "utils"
import type { SearchPageProps, SearchResponse, SearchResult } from "./types"
import { PAGE_SIZE, SUGGESTION_COUNT } from "./types"
import { LogoMark, HeadingChevron } from "./icons"
import SearchField from "./SearchField"
import RecommendedSearches from "./RecommendedSearches"
import CategoryFilter from "./CategoryFilter"
import ResultsList from "./ResultsList"
import Pagination from "./Pagination"
import NoResults from "./NoResults"
import {
  Page,
  Hero,
  Logo,
  HeadingRow,
  HeadingChevronWrap,
  Heading,
  ResultsSection,
  LoadingWrapper,
  Spinner,
} from "./styles"

async function fetchSearch(params: {
  q: string
  locale: string
  page: number
  pageSize: number
  category?: string
}): Promise<SearchResponse> {
  const search = new URLSearchParams({
    q: params.q,
    locale: params.locale,
    page: String(params.page),
    pageSize: String(params.pageSize),
  })
  if (params.category) search.set("category", params.category)

  const res = await fetch(`/app-api/search?${search.toString()}`)
  if (!res.ok) throw new Error(`Search failed: ${res.status}`)
  return res.json()
}

const SearchPage: FC<SearchPageProps> = ({
  locale,
  recommended,
  categories,
  initialQuery = "",
  initialPage = 1,
  initialCategory = "",
}) => {
  const t = useTranslations("Search")
  const router = useRouter()
  const queryClient = useQueryClient()

  const [inputValue, setInputValue] = useState(initialQuery)
  const [committedQuery, setCommittedQuery] = useState(initialQuery.trim())
  const [page, setPage] = useState(initialPage > 0 ? initialPage : 1)
  const [categoryKey, setCategoryKey] = useState(initialCategory)
  const [suggestQuery, setSuggestQuery] = useState("")

  // EGDE-577: syncUrl below only replaceState-s (no server round-trip), so
  // after navigating to a result and pressing Back, Next.js restores the
  // cached RSC payload from the first visit and the initial* props arrive
  // stale relative to the URL. Re-read the live URL once on mount so
  // returning to /search?q=… shows the results again.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const q = params.get("q") ?? ""
    setInputValue(q)
    setCommittedQuery(q.trim())
    setPage(Math.max(1, Math.floor(Number(params.get("page"))) || 1))
    setCategoryKey(params.get("category") ?? "")
  }, [])

  // Keep the browser URL in sync so a search is shareable / deep-linkable
  // without triggering a server round-trip.
  const syncUrl = useCallback((q: string, p: number, category: string) => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams()
    if (q) params.set("q", q)
    if (p > 1) params.set("page", String(p))
    if (category) params.set("category", category)
    const qs = params.toString()
    const url = `${window.location.pathname}${qs ? `?${qs}` : ""}`
    window.history.replaceState(null, "", url)
  }, [])

  // --- Results ---
  const resultsQuery = useQuery({
    queryKey: ["search-results", committedQuery, page, categoryKey, locale],
    queryFn: () =>
      fetchSearch({
        q: committedQuery,
        locale,
        page,
        pageSize: PAGE_SIZE,
        category: categoryKey || undefined,
      }),
    enabled: committedQuery.length > 0,
    placeholderData: keepPreviousData,
  })

  // --- Autocomplete suggestions (debounced) ---
  const suggestionsQuery = useQuery({
    queryKey: ["search-suggest", suggestQuery, locale],
    queryFn: () =>
      fetchSearch({
        q: suggestQuery,
        locale,
        page: 1,
        pageSize: SUGGESTION_COUNT,
      }),
    enabled: suggestQuery.trim().length > 1,
  })

  const debouncedSuggest = useMemo(
    () => debounce((value: string) => setSuggestQuery(value), 250),
    [],
  )
  useEffect(() => () => debouncedSuggest.clear(), [debouncedSuggest])

  // --- Handlers ---
  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value)
      debouncedSuggest(value)
    },
    [debouncedSuggest],
  )

  const handleSubmit = useCallback(
    (value: string) => {
      const q = value.trim()
      setCommittedQuery(q)
      setPage(1)
      // A fresh search starts unfiltered — don't carry the previously selected
      // category over to the new query (EGDE-402).
      setCategoryKey("")
      setSuggestQuery("")
      debouncedSuggest.clear()
      syncUrl(q, 1, "")
      if (q) searchSubmit(q)
    },
    [debouncedSuggest, syncUrl],
  )

  const handleClear = useCallback(() => {
    setInputValue("")
    setCommittedQuery("")
    setSuggestQuery("")
    setPage(1)
    setCategoryKey("")
    debouncedSuggest.clear()
    // EGDE-400: the X is a full reset — drop cached suggestion results too.
    // Retyping the exact same term otherwise resolves from the query cache
    // without hitting the network, which QA read as "suggestions stopped
    // working" when watching the requests panel.
    queryClient.removeQueries({ queryKey: ["search-suggest"] })
    syncUrl("", 1, "")
  }, [debouncedSuggest, queryClient, syncUrl])

  const handleSelectSuggestion = useCallback(
    (result: SearchResult) => {
      searchResultClick(inputValue.trim() || committedQuery, result.title)
      router.push(result.url)
    },
    [committedQuery, inputValue, router],
  )

  const handleRecommended = useCallback(
    (term: string) => {
      setInputValue(term)
      handleSubmit(term)
    },
    [handleSubmit],
  )

  const handleCategory = useCallback(
    (key: string) => {
      setCategoryKey(key)
      setPage(1)
      syncUrl(committedQuery, 1, key)
    },
    [committedQuery, syncUrl],
  )

  const handlePageChange = useCallback(
    (next: number) => {
      setPage(next)
      syncUrl(committedQuery, next, categoryKey)
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" })
      }
    },
    [categoryKey, committedQuery, syncUrl],
  )

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      searchResultClick(committedQuery, result.title)
    },
    [committedQuery],
  )

  // --- Derived render state ---
  const hasQuery = committedQuery.length > 0
  const data = resultsQuery.data
  const isInitialLoading = resultsQuery.isLoading && hasQuery && !data
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)
  const suggestions = suggestionsQuery.data?.results ?? []

  // EGDE-660: offer only the categories this query can actually fill. Every
  // configured category used to be listed, so a search with hits in one section
  // still showed the rest and clicking them dead-ended on "no results". The
  // selected category stays listed even at zero so a deep link like
  // ?q=x&category=jobs still shows which filter is applied and can be cleared.
  const visibleCategories = useMemo(() => {
    const counts = data?.categoryCounts
    if (!counts) return categories
    // A category key is the comma-separated alias list the API filters on, so a
    // category is fillable when ANY of its aliases has a hit — the same test the
    // route applies to `categoryAlias`.
    const hits = (key: string) =>
      key
        .split(",")
        .map((alias) => alias.trim().toLowerCase())
        .filter(Boolean)
        .reduce((sum, alias) => sum + (counts[alias] ?? 0), 0)
    return categories.filter(
      (category) => hits(category.key) > 0 || category.key === categoryKey,
    )
  }, [categories, data?.categoryCounts, categoryKey])

  return (
    <Page>
      <Hero>
        <Logo aria-hidden="true">
          <LogoMark />
        </Logo>
        <HeadingRow>
          <HeadingChevronWrap aria-hidden="true">
            <HeadingChevron />
          </HeadingChevronWrap>
          <Heading>{t("heading")}</Heading>
        </HeadingRow>
        <SearchField
          value={inputValue}
          placeholder={t("placeholder")}
          ariaLabel={t("ariaSearch")}
          ariaClear={t("ariaClear")}
          ariaSuggestions={t("ariaSuggestions")}
          suggestions={suggestions}
          onChange={handleInputChange}
          onSubmit={handleSubmit}
          onClear={handleClear}
          onSelectSuggestion={handleSelectSuggestion}
        />
      </Hero>

      {!hasQuery && (
        <RecommendedSearches
          heading={t("recommendedHeading")}
          items={recommended}
          onSelect={handleRecommended}
        />
      )}

      {hasQuery && isInitialLoading && (
        <LoadingWrapper>
          <Spinner aria-label={t("loading")} />
        </LoadingWrapper>
      )}

      {hasQuery && !isInitialLoading && total > 0 && data && (
        <ResultsSection>
          <CategoryFilter
            categories={visibleCategories}
            activeKey={categoryKey}
            allLabel={t("all")}
            onSelect={handleCategory}
          />
          <ResultsList
            count={t.rich("resultsCount", {
              count: total,
              b: (chunks) => <b>{chunks}</b>,
            })}
            results={data.results}
            onResultClick={handleResultClick}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            prevLabel={t("prevPage")}
            nextLabel={t("nextPage")}
            onChange={handlePageChange}
          />
        </ResultsSection>
      )}

      {hasQuery && !isInitialLoading && total === 0 && (
        <NoResults
          title={t.rich("noResultsTitle", {
            b: (chunks) => <b>{chunks}</b>,
          })}
          resetLabel={t("reset")}
          onReset={handleClear}
        />
      )}
    </Page>
  )
}

export default SearchPage
